<script setup>
import { useRouter } from 'vue-router'
import { DxDataGrid } from 'devextreme-vue/data-grid'
import { getI18n } from '@/plugins/i18n'
import { useWebSiteStore } from '@/stores/web-site.store'
import { useGridRef, useDefaultGridOptions, useLookup } from '@/composables/data-grid'
import { useRoiComparisonFormat } from '@/composables/roi-comparison'

import RoiComparisonLibraryToggles from '@/components/roi/RoiComparisonLibraryToggles.vue'
import RoiComparisonView from '@/components/roi/RoiComparisonView.vue'
import RoiComparisonAvatarStack from '@/components/roi/RoiComparisonAvatarStack.vue'

const props = defineProps({
  comparison: {
    type: Object,
    required: true,
  },
})

const emit = defineEmits(['open', 'edit', 'clone', 'add', 'delete'])

const i18n = getI18n()
const webSiteStore = useWebSiteStore()
const router = useRouter()
const { TYPE, definitions, typeLabel, itemName, itemRoute } = useRoiComparisonFormat()
const { gridRef } = useGridRef()

const canManage = (e) => webSiteStore.canManageOwned(e.row?.data, webSiteStore.permissionFlagEnum.COMPANY_SETTINGS)

const expand = (id) => gridRef.value.instance.expandRow(id)

const toggle = (layout) => {
  const instance = gridRef.value.instance
  if (instance.isRowExpanded(layout.id)) instance.collapseRow(layout.id)
  else expand(layout.id)
}

const onRowExpanding = (e) => {
  if (props.comparison.current?.id !== e.key) emit('open', props.comparison.savedComparisons.find(layout => layout.id === e.key))
}

const columns = [
  {
    type: 'buttons',
    buttons: [
      { icon: 'edit', hint: i18n.t('common.edit'), visible: canManage, onClick: (e) => emit('edit', e.row.data) },
      { icon: 'trash', hint: i18n.t('common.delete'), visible: canManage, onClick: (e) => emit('delete', e.row.data) },
      { icon: 'mdi mdi-content-copy', hint: i18n.t('common.clone'), onClick: (e) => emit('clone', e.row.data) },
    ],
    fixed: false,
    visibleIndex: -1,
    showInColumnChooser: false,
    width: '7rem',
  },
  { name: 'images', caption: '', cellTemplate: 'images-cell', allowSorting: false, allowFiltering: false, width: '7rem' },
  { dataField: 'is_shared', caption: i18n.t('common.shared'), dataType: 'boolean', cellTemplate: 'shared-cell', headerCellTemplate: 'shared-header', allowFiltering: false, width: '3.5rem' },
  { dataField: 'name', caption: i18n.t('common.name'), cellTemplate: 'name-cell', width: '20rem' },
  {
    dataField: 'data.type',
    caption: i18n.t('common.type'),
    lookup: useLookup({ dataSource: Object.values(TYPE).map(type => ({ id: type, name: typeLabel(type) })), displayExpr: 'name', sort: 'name' }),
    width: '9rem',
  },
  { dataField: 'created_by_name', caption: i18n.t('common.createdBy'), width: '10rem' },
  { name: 'items', caption: i18n.t('common.items'), dataType: 'string', allowFiltering: true, allowSorting: true, calculateCellValue: (row) => row.data.names?.join(' · '), cellTemplate: 'items-cell' },
]

const gridOptions = useDefaultGridOptions({
  id: 'roi-comparisons-grid-v5',
  keyExpr: 'id',
  columns,
  remoteOperations: false,
  exportFileName: i18n.t('menu.roi_comparison'),
  viewToolbarItems: [{ location: 'after', template: 'library-toggles' }],
  hideRefreshButton: true,
  addButtons: [
    { icon: 'mdi-plus', hint: i18n.t('common.add'), onClick: () => emit('add', false) },
    { icon: 'mdi-flash', hint: i18n.t('common.quickCreate'), onClick: () => emit('add', true) },
  ],
  masterDetail: {
    enabled: true,
    template: 'master-detail',
  },
  onRowExpanding,
  onRowDblClick: (e) => toggle(e.data),
})

defineExpose({ expand })
</script>

<template>
  <div>
    <dx-data-grid
      ref="gridRef"
      v-bind="gridOptions"
      :data-source="comparison.visibleComparisons"
    >
      <template #library-toggles>
        <roi-comparison-library-toggles />
      </template>
      <template #name-cell="{ data }">
        <a href="#" class="grid-link" @click.prevent.stop="toggle(data.data)">
          {{ data.value }}
        </a>
      </template>
      <template #master-detail="{ data: row }">
        <roi-comparison-view
          v-if="comparison.current?.id === row.key"
          :comparison="comparison"
        />
        <div v-else class="relative-position" style="height: 6rem;">
          <q-inner-loading showing />
        </div>
      </template>
      <template #images-cell="{ data }">
        <roi-comparison-avatar-stack :type="data.data.data.type" :items="comparison.layoutItems(data.data)" />
      </template>
      <template #items-cell="{ data }">
        <div class="row wrap" style="column-gap: .75rem;">
          <a
            v-for="item in comparison.layoutItems(data.data)"
            :key="item.id"
            href="#"
            class="grid-link"
            @click.prevent.stop="router.push(itemRoute(data.data.data.type, item))"
          >
            <i :class="`mdi ${definitions[data.data.data.type].icon}`" />{{ itemName(data.data.data.type, item) }}
          </a>
        </div>
      </template>
      <template #shared-header>
        <q-icon name="mdi-account-group" size="xs">
          <q-tooltip>{{ i18n.t('common.shared') }}</q-tooltip>
        </q-icon>
      </template>
      <template #shared-cell="{ data }">
        <q-icon
          v-if="data.value"
          name="mdi-account-group"
          color="primary"
          size="xs"
        />
      </template>
    </dx-data-grid>
  </div>
</template>
